/**
 * PlannerRepository — planner items are user-owned; every write resolves
 * with the confirmed server row.
 */
import { requireSupabase } from '@/lib/supabase/client'
import {
  plannerItemFromRow,
  plannerItemToInsert,
  plannerItemToPatch,
  type PlannerItemRow,
} from '@/lib/supabase/mappers'
import type { PlannerItem } from '@/types'
import type { PlannerRepository } from '../interfaces'
import { assertRowReturned, fail, requireUserId } from './helpers'

export const plannerRepository: PlannerRepository = {
  async listForDate(date) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('planner_items')
      .select('*')
      .eq('user_id', userId)
      .eq('date', date)
      .order('start_minute', { ascending: true })
    if (error) fail(error)
    return ((data ?? []) as PlannerItemRow[]).map(plannerItemFromRow)
  },

  async listForRange(startDate, endDate) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('planner_items')
      .select('*')
      .eq('user_id', userId)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: true })
      .order('start_minute', { ascending: true })
    if (error) fail(error)
    return ((data ?? []) as PlannerItemRow[]).map(plannerItemFromRow)
  },

  async create(item) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('planner_items')
      .insert(plannerItemToInsert(item, userId))
      .select()
      .single()
    return plannerItemFromRow(assertRowReturned(data, error) as PlannerItemRow)
  },

  async update(id, patch): Promise<PlannerItem> {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('planner_items')
      .update(plannerItemToPatch(patch))
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single()
    return plannerItemFromRow(assertRowReturned(data, error) as PlannerItemRow)
  },

  async remove(id) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { error } = await supabase.from('planner_items').delete().eq('id', id).eq('user_id', userId)
    if (error) fail(error)
  },
}
